/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  forwardRef,
  useRef,
  type ForwardedRef,
  type PointerEvent,
  type ReactNode,
} from "react";
import { mergeProps, useFocusRing, usePress } from "react-aria";

import { cssFns, type Style } from "@superweb/css";
import { useLocale } from "@superweb/intl";
import { useTypo, useUiColors, useUiShadows } from "@superweb/ui";

import { Tooltip, useTooltip } from "../../tooltip/tooltip";
import type { LinkProps } from "../types";

export const zIndexMap = {
  headerFirstColumn: "6",
  headerColumn: "5",
  stickyFirstColumn: "4",
  stickyColumn: "3",
  stickyRow: "3",
};

export const useBaseCellStyle = ({
  isFocusVisible,
  isHeaderCell,
  position,
  variant = "fill",
  isPinned = false,
}: {
  isFocusVisible: boolean;
  isHeaderCell?: boolean;
  isPinned?: boolean;
  position?: {
    firstLeft?: boolean;
    firstRight?: boolean;
    lastLeft?: boolean;
    lastRight?: boolean;
  };
  variant?: "ghost" | "fill";
}): Style => {
  const uiColors = useUiColors();

  const disableBorderRadius = isPinned;

  const bottomBorder: Style =
    variant === "fill"
      ? isHeaderCell
        ? {
            borderBlockEndColor: cssFns.setOpacity(uiColors.text, 0.5),
            borderBlockEndStyle: "solid",
            borderBlockEndWidth: "1px",
          }
        : {
            borderBlockEndColor: uiColors.controlMinor,
            borderBlockEndStyle: "solid",
            borderBlockEndWidth: "1px",
          }
      : {};

  return {
    ...cssFns.padding("16px"),
    ...cssFns.overflow("hidden"),
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    minWidth: "32px",
    maxWidth: "320px",
    boxSizing: "border-box",
    outlineStyle: "none",
    ...(isFocusVisible &&
      cssFns.boxShadow({
        inset: true,
        spreadRadius: "2px",
        color: uiColors.focus,
      })),
    ...(position?.firstLeft &&
      !disableBorderRadius && {
        borderStartStartRadius: "26px",
      }),
    ...(position?.firstRight &&
      !disableBorderRadius && {
        borderStartEndRadius: "26px",
      }),
    ...(position?.lastLeft &&
      !disableBorderRadius && {
        borderEndStartRadius: "26px",
      }),
    ...(position?.lastRight &&
      !disableBorderRadius && {
        borderEndEndRadius: "26px",
      }),
    ...bottomBorder,
  };
};

export type BaseProps = {
  children: ReactNode;
  align: "start" | "end";
  index: number;
  tabIndex?: number;
  isHovered?: boolean;
  isFooter?: boolean;
  isTopPinned?: boolean;
  isBottomPinned?: boolean;
  isStickyFirstColumn?: boolean;
  stickyFirstColumnShift?: number;
  updateFocusPointer: () => void;
};

const stickyColumnBoxShadowMap = {
  ltr: {
    clipPath: "inset(0px -28px -10px 0px)",
    boxShadow: "6px 0px 6px 0px rgba(0, 0, 0, 0.12)",
  },
  rtl: {
    clipPath: "inset(0px 0px -10px -28px)",
    boxShadow: "-6px 0px 6px 0px rgba(0, 0, 0, 0.12)",
  },
};

export const useCellStyle = ({
  index,
  variant,
  align = "start",
  isHovered = false,
  isFocusVisible = false,
  isFooter = false,
  isStickyFirstColumn = false,
  stickyFirstColumnShift = 0,
  isTopPinned = false,
  isBottomPinned = false,
  isLastRow = false,
  clickable = false,
  position,
  isFirstColumn = false,
  level = 1,
}: {
  index: number;
  variant: "fill" | "ghost";
  align?: "start" | "end";
  isHovered: boolean;
  isFocusVisible: boolean;
  isFooter?: boolean;
  isStickyFirstColumn?: boolean;
  stickyFirstColumnShift?: number;
  isTopPinned?: boolean;
  isBottomPinned?: boolean;
  isLastRow?: boolean;
  clickable?: boolean;
  position?: {
    firstLeft?: boolean;
    firstRight?: boolean;
    lastLeft?: boolean;
    lastRight?: boolean;
  };
  isFirstColumn?: boolean;
  level?: number;
}): Style => {
  const typo = useTypo();
  const uiColors = useUiColors();
  const uiShadows = useUiShadows();

  const isPinned = isTopPinned || isBottomPinned;

  const locale = useLocale();

  const stickyColumnBoxShadow =
    stickyColumnBoxShadowMap[locale.textInfo.direction];

  let zIndex = "1";
  if (isPinned && !isStickyFirstColumn) {
    zIndex = zIndexMap.stickyRow;
  }
  if (isStickyFirstColumn && index === 1) {
    zIndex = zIndexMap.stickyColumn;
  }

  let insetInlineStart = `${stickyFirstColumnShift + 24}px`;
  if (index === 1) {
    insetInlineStart = `${stickyFirstColumnShift}px`;
  }

  const showStickyBoxShadow =
    isStickyFirstColumn && index === 1 && !isFocusVisible;

  return {
    ...useBaseCellStyle({
      isFocusVisible,
      isHeaderCell: false,
      ...(variant === "ghost" && { position }),
      variant,
      isPinned,
    }),

    ...(variant === "ghost" &&
      !isTopPinned &&
      !isBottomPinned &&
      !isLastRow && {
        borderBottomStyle: "solid",
        borderBottomColor: cssFns.setOpacity(uiColors.line, 0.1),
        borderBottomWidth: "2px",
      }),

    ...typo({
      level: "body2",
      weight: "regular",
      density: "tight",
    }),
    ...cssFns.padding("0"),
    ...(isFirstColumn
      ? { paddingInlineStart: `${(level - 1) * 24}px` }
      : undefined),
    cursor: clickable ? "pointer" : "default",
    textAlign: align,
    backgroundColor: isPinned
      ? uiColors.background
      : isHovered || isFooter
        ? cssFns.blend(uiColors.background, uiColors.hover)
        : uiColors.background,
    color: uiColors.text,
    zIndex,
    ...(isStickyFirstColumn && {
      position: "sticky",
      insetInlineStart,
    }),
    ...(showStickyBoxShadow && stickyColumnBoxShadow),
    // https://github.com/mdn/content/issues/29839
    // workaround for safari box-shadow <tr>
    ...(isTopPinned &&
      !isStickyFirstColumn && {
        boxShadow: uiShadows.bottomNormal,
        clipPath: `inset(0 0 -40px 0);`,
      }),
    ...(isBottomPinned &&
      !isStickyFirstColumn && {
        boxShadow: uiShadows.topNormal,
        clipPath: `inset(-40px 0 0 0);`,
      }),
  };
};

export const Cell = forwardRef(
  (
    {
      children,
      align = "start",
      isHovered = false,
      isFooter = false,
      isStickyFirstColumn = false,
      stickyFirstColumnShift = 0,
      index,
      tabIndex,
      onPress,
      updateFocusPointer,
      tooltip,
      linkProps,
      variant = "fill",
      position,
      isTopPinned = false,
      isBottomPinned = false,
      isLastRow = false,
      isFirstColumn = false,
      level,
    }: BaseProps & {
      linkProps?: LinkProps;
      onPress?: () => void;
      tooltip?: ReactNode;
      variant?: "fill" | "ghost";
      isLastRow?: boolean;
      position: {
        firstLeft?: boolean;
        firstRight?: boolean;
        lastLeft?: boolean;
        lastRight?: boolean;
      };
      isFirstColumn?: boolean;
      level?: number;
    },
    ref: ForwardedRef<HTMLTableCellElement>,
  ) => {
    const uiColors = useUiColors();

    const { focusProps, isFocusVisible } = useFocusRing();
    const { pressProps } = usePress({
      onPress: () => {
        updateFocusPointer();
        onPress?.();
      },
    });

    const targetRef = useRef(null);
    const {
      triggerProps,
      tooltipProps,
      state: tooltipState,
    } = useTooltip(targetRef, {
      delay: 1200,
      shouldCloseOnScroll: true,
    });

    let cellProps = onPress ? mergeProps(focusProps, pressProps) : focusProps;
    cellProps = tooltip ? mergeProps(cellProps, triggerProps) : cellProps;

    const cellStyle = useCellStyle({
      index,
      variant,
      align,
      isHovered,
      isFocusVisible,
      isFooter,
      isStickyFirstColumn,
      stickyFirstColumnShift,
      isTopPinned,
      isBottomPinned,
      isLastRow,
      clickable: Boolean(linkProps || onPress),
      position,
      isFirstColumn,
      level,
    });

    const style: Style = {
      ...cssFns.overflow("hidden"),
      textOverflow: "ellipsis",
      display: "block",
      ...(variant === "ghost" && { lineHeight: "24px" }),
    };

    return (
      <td css={cellStyle} aria-colindex={index} ref={ref}>
        {linkProps ? (
          <a
            {...mergeProps(focusProps, pressProps, triggerProps)}
            href={linkProps.href}
            onClick={(e: PointerEvent<HTMLAnchorElement>) => {
              updateFocusPointer();
              onPress?.();
              linkProps.onClick?.(e);
            }}
            tabIndex={tabIndex}
            css={{
              color: uiColors.text,
              textDecorationLine: "none",
              outlineStyle: "none",
              ...cssFns.padding("16px"),
              ...style,
            }}
          >
            <span ref={targetRef}>{children}</span>
          </a>
        ) : (
          <div
            {...cellProps}
            tabIndex={tabIndex}
            css={{
              outlineStyle: "none",
              ...cssFns.padding("16px"),
              ...style,
            }}
          >
            <span ref={targetRef}>{children}</span>
          </div>
        )}

        {tooltip && tooltipState.isOpen ? (
          <Tooltip
            state={tooltipState}
            tooltipProps={tooltipProps}
            targetRef={targetRef}
            variant="info"
          >
            {tooltip}
          </Tooltip>
        ) : undefined}
      </td>
    );
  },
);
